import React from 'react';
import * as LanguageContext from '../../contexts/LanguageContext';
import { formatApprox, useDisplayCurrency } from '../../utils/displayCurrency';
import { formatAed } from '../../utils/vat';

/** The shop language ('en' without a provider; tolerant of partial test mocks). */
const languageHook = () => LanguageContext.useOptionalLanguage || LanguageContext.useLanguage || (() => null);
export const useShopLanguage = () => (languageHook()() || {}).language || 'en';

/**
 * (aed) → "1,250 AED", or "≈ SAR 1,276.50" when the shopper picked another
 * display currency. Display only: never for an amount that is charged (bag
 * total, checkout, orders, invoices, payments, delivery fees, instalments).
 */
export const useMoney = () => {
  const { cur } = useDisplayCurrency();
  const lang = useShopLanguage();
  // withAed: "≈ SAR 51.05 (50 AED)" where the AED figure also matters (free-delivery gap).
  return (aed, withAed = false) => {
    if (!cur) return formatAed(aed);
    const text = withAed ? `${formatApprox(aed, cur, lang)} (${formatAed(aed)})` : formatApprox(aed, cur, lang);
    // Inside Arabic sentences: an LTR isolate keeps "≈ 43.887 د.ك (525 AED)" in one piece.
    return lang === 'ar' ? `\u2066${text}\u2069` : text;
  };
};

/** null in AED, else (aed) → "≈ SAR 536.08 (525 AED)" (thresholds and caps that are not a charge). */
export const useApprox = () => {
  const money = useMoney();
  return useDisplayCurrency().cur ? (aed) => money(aed, true) : undefined;
};

/** An inline shop price (LTR); hovering a converted one shows the AED price. */
const Money = ({ aed, className }) => {
  const { cur } = useDisplayCurrency();
  const lang = useShopLanguage();
  return (
    <bdi dir="ltr" className={className} title={cur ? formatAed(aed) : undefined}>
      {cur ? formatApprox(aed, cur, lang) : formatAed(aed)}
    </bdi>
  );
};

export default Money;
